import { format, isSameMonth, isSameYear } from "date-fns";

type DateInput = Date | string | number | null | undefined;

const timeFormatting = (timeString: string) => {
  const [hourString = "0", minute = "00"] = timeString.split(":");
  const hour = +hourString % 24;
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;

  return `${formattedHour}:${minute} ${period}`;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_MINUTE = 60 * 1000;

// Returns a valid Date or null (guards Invalid Date -> toISOString RangeError).
const toValidDate = (date: DateInput): Date | null => {
  if (date === null || date === undefined || date === "") return null;
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Booking `from` / `to` arrive from the API as calendar days wrapped in UTC
 * timestamps: `2026-08-11T00:00:01Z` (check-in day) and `2026-08-12T23:59:59Z`
 * (check-out day). Only the date part carries meaning — the times are just
 * start/end-of-day markers.
 *
 * Read as plain instants they misreport in two ways, both of which reached the
 * UI for an 11 → 12 Aug, one-night booking:
 *  - the raw gap spans ~48h, so it counted as "2 days" and the per-night price
 *    came out halved (1799 → 900);
 *  - formatting in local time rolls `23:59:59Z` into the next day anywhere east
 *    of UTC, so Asia/Dhaka rendered "11 - 13 Aug 2026".
 *
 * So both helpers below work off the UTC date. Date objects (e.g. straight from
 * a date picker) already represent a local calendar day and are left alone.
 */
const utcDayStart = (value: DateInput) => {
  const d = toValidDate(value);
  if (!d) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * A Date whose *local* fields equal the UTC fields of `value`, so `format()`
 * prints the UTC calendar day. Applied to ISO strings only.
 */
const asUtcCalendarDate = (value: DateInput) => {
  if (typeof value !== "string") return toValidDate(value);
  const d = toValidDate(value);
  if (!d) return null;
  return new Date(d.getTime() + d.getTimezoneOffset() * MS_PER_MINUTE);
};

/** Whole calendar days between two dates — nights, for a booking. */
const dateDifference = (a: DateInput, b: DateInput) => {
  const start = utcDayStart(a);
  const end = utcDayStart(b);
  if (start === null || end === null) return 0;
  return Math.round(Math.abs(end - start) / MS_PER_DAY);
};

const formatDate = (date: DateInput, template = "MMM dd, yyyy") => {
  const d = toValidDate(date);
  return d ? format(d, template) : "";
};

const daysOfTheYear = (value: Date | string | number) => {
  const date = new Date(value);
  const totalDays =
    date.getFullYear() * 365 + date.getMonth() * 30 + date.getDate();
  return totalDays;
};

const formatYearDate = (date: DateInput, template = "yyyy-MM-dd") => {
  const d = toValidDate(date);
  return d ? format(d, template) : "";
};

const formatTime = (date: DateInput, template = "hh:mm a") => {
  const d = toValidDate(date);
  return d ? format(d, template) : "";
};

const formateMonthDate = (date: DateInput, template = "MMM dd") => {
  const d = toValidDate(date);
  return d ? format(d, template) : "";
};

const formateDateMonth = (date: DateInput, template = "dd MMM") => {
  const d = toValidDate(date);
  return d ? format(d, template) : "";
};
const formatMonthDateFullYear = (
  date: DateInput,
  template = "dd MMM, yyyy",
) => {
  const d = toValidDate(date);
  return d ? format(d, template) : "";
};
const formatDateRange = (startDate: DateInput, endDate: DateInput) => {
  // Normalize to Date objects, reading ISO strings as UTC calendar days.
  const start = asUtcCalendarDate(startDate);
  const end = asUtcCalendarDate(endDate);
  if (!start || !end) return "";

  const sameMonth = isSameMonth(start, end);
  const sameYear = isSameYear(start, end);

  if (sameMonth && sameYear) {
    return `${format(start, "dd")} - ${format(end, "dd MMM yyyy")}`;
  } else {
    return `${format(start, "dd MMM yy")} - ${format(end, "dd MMM yy")}`;
  }
};

const dateSubtract = (checkingDate: Date | string, day: number) => {
  const date = new Date(checkingDate);
  date.setDate(date.getDate() - day);
  return formatDate(date);
};

const dateAdd = (givenDate: Date | string, day: number) => {
  const date = new Date(givenDate);
  date.setDate(date.getDate() + day);
  return formatYearDate(date);
};

/** When the booking's status last changed, or its creation for one never updated. */
const bookingDateNullChecker = (data: {
  status_updated_at?: string | null;
  created_at: string;
}) => data.status_updated_at ?? data.created_at;

const getDiscount = (data: {
  more_nights_discount: number;
  discount: number;
}) => Math.ceil(data.more_nights_discount + data.discount);

export {
  timeFormatting,
  dateDifference,
  formatDate,
  daysOfTheYear,
  formatYearDate,
  formatTime,
  formateMonthDate,
  formateDateMonth,
  formatMonthDateFullYear,
  formatDateRange,
  dateSubtract,
  dateAdd,
  bookingDateNullChecker,
  getDiscount,
};
